import { useEffect, useState } from 'react';
import { can, checkOrder, jstDate, lockKeys, type BatchDetail, type DeliveryRow, type Driver, type RoutePlan } from '@torapon/shared';
import { RoutePlanBox } from './RoutePlanBox';
import { api, HttpError } from '../lib/api';
import { Dialog, useToast } from './ui';
import { PhotoDialog } from './Photos';
import { useNames } from '../lib/directory';
import { useMe } from '../lib/me';
import { showPlacesToDriver } from '../lib/fs';
import { LockBanner, useEditLock, type EditLock } from '../lib/locks';

type Names = ReturnType<typeof useNames>;
/** 「積む → 届ける」の表示（場所の名前は Firestore から引く） */
export const route = (r: DeliveryRow, n: Names) => `${r.pickupPlaceIds.map((id) => n.place(id)).join('・')} → ${n.place(r.dropPlaceId)}`;

const FINAL = ['dropped', 'canceled', 'reassigned'];

/** 配送 1 件の操作：回る順番の並べ替え・付け替え（割り当て）・取り消し */
export function DeliveryActions({ row, onChanged }: { row: DeliveryRow; onChanged: () => void }) {
  const [open, setOpen] = useState<'order' | 'reassign' | 'cancel' | 'photos' | null>(null);
  const me = useMe();
  const photos = !!me && can(me.role, 'viewPhotos');
  // 順番・付け替え・取り消しを開いている間は、そのドライバーのその日の予定をロックする
  const editing = open === 'order' || open === 'reassign' || open === 'cancel';
  const lock = useEditLock(editing && row.driverId ? lockKeys.driverDay(row.driverId, jstDate(row.createdAt)) : null);
  const photoButton = photos && <><button className="btn sm" onClick={() => setOpen('photos')}>写真</button>{open === 'photos' && <PhotoDialog title={`${row.deliveryNo} の写真`} query={`deliveryNo=${row.deliveryNo}`} onClose={() => setOpen(null)} />}</>;
  if (FINAL.includes(row.status)) return <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>{photoButton}</span>;
  return (
    <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
      {photoButton}
      <button className="btn sm" onClick={() => setOpen('order')}>順番</button>
      <button className="btn sm" onClick={() => setOpen('reassign')}>{row.status === 'unassigned' ? '割り当て' : '付け替え'}</button>
      <button className="btn sm danger" onClick={() => setOpen('cancel')}>取り消し</button>
      {open === 'order' && <OrderDialog batchId={row.batchId} lock={lock} onClose={() => setOpen(null)} onDone={onChanged} />}
      {open === 'reassign' && <ReassignDialog row={row} lock={lock} onClose={() => setOpen(null)} onDone={onChanged} />}
      {open === 'cancel' && <CancelDialog row={row} lock={lock} onClose={() => setOpen(null)} onDone={onChanged} />}
    </span>
  );
}

function OrderDialog({ batchId, lock, onClose, onDone }: { batchId: string; lock: EditLock; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [d, setD] = useState<BatchDetail | null>(null);
  const [order, setOrder] = useState<number[]>([]);
  const [err, setErr] = useState('');
  const [plan, setPlan] = useState<RoutePlan | null>(null);
  useEffect(() => { api.get<BatchDetail>(`/v1/office/batches/${batchId}`).then((x) => { setD(x); setOrder(x.visits.map((v) => v.seq)); }); }, [batchId]);
  useEffect(() => { api.get<RoutePlan>(`/v1/office/batches/${batchId}/plan`).then(setPlan).catch(() => {}); }, [batchId]);
  const applyPlan = () => { if (d && plan) setOrder([...d.visits.filter((v) => v.done).map((v) => v.seq), ...plan.recommended.order]); };
  const names = useNames();
  const by = new Map((d?.visits ?? []).map((v) => [v.seq, v]));
  const move = (i: number, dir: -1 | 1) => setOrder((o) => { const n = [...o]; const j = i + dir; if (j < 0 || j >= n.length) return o; [n[i], n[j]] = [n[j]!, n[i]!]; return n; });
  useEffect(() => { if (d) setErr(checkOrder(d.visits, order) ?? ''); }, [d, order]);
  const save = async () => {
    try { await api.post(`/v1/office/batches/${batchId}/order`, { order }, { ifMatch: d!.version }); toast('回る順番を変えました'); onDone(); onClose(); }
    catch (e) { setErr(e instanceof HttpError ? e.message : '保存できませんでした'); }
  };
  return (
    <Dialog title="回る順番を変える" onClose={onClose}>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>同じ配送は、積む場所を先に回ってから届けます。済んだ行き先は動かせません。</p>
      {plan && plan.current.order.length > 1 && <RoutePlanBox plan={plan} onApply={applyPlan} />}
      {!d ? <p className="muted">読み込み中…</p> : (
        <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 6 }} aria-label="回る順番">
          {order.map((seq, i) => { const v = by.get(seq)!; return (
            <li key={seq} style={{ display: 'grid', gridTemplateColumns: '24px 1fr auto', gap: 8, alignItems: 'center', background: 'var(--surface-2)', borderRadius: 10, padding: '8px 10px', opacity: v.done ? 0.5 : 1 }}>
              <b style={{ width: 22, height: 22, borderRadius: v.kind === 'drop' ? '50%' : 5, background: v.kind === 'drop' ? 'var(--go)' : '#000', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 12 }}>{v.kind === 'pickup' ? 'P' : 'D'}</b>
              <span><b>{names.place(v.placeId)}</b><small className="muted" style={{ display: 'block' }}>{v.kind === 'pickup' ? '積む' : '届ける'}・{v.deliveryNos.join('・')}{v.dueAt ? `・${v.dueAt} 必着` : ''}{v.done ? '・済み' : ''}</small></span>
              <span style={{ display: 'flex', gap: 4 }}><button className="btn sm" aria-label={`${names.place(v.placeId)} を上へ`} disabled={v.done} onClick={() => move(i, -1)}>↑</button><button className="btn sm" aria-label={`${names.place(v.placeId)} を下へ`} disabled={v.done} onClick={() => move(i, 1)}>↓</button></span>
            </li>
          ); })}
        </ol>
      )}
      <LockBanner lock={lock} what="このドライバーのこの日の予定" />
      {err && <p className="err" role="alert">{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={onClose}>やめる</button><button className="btn pri" disabled={!!err || !d || !lock.mine} onClick={save}>この順番にする</button></div>
    </Dialog>
  );
}

function ReassignDialog({ row, lock, onClose, onDone }: { row: DeliveryRow; lock: EditLock; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const me = useMe();
  const names = useNames();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [to, setTo] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => { api.get<{ drivers: Driver[] }>('/v1/office/drivers').then((r) => setDrivers(r.drivers.filter((d) => d.id !== row.driverId))); }, [row.driverId]);
  const save = async () => {
    try { const r = await api.post<{ deliveryNo: string }>(`/v1/office/deliveries/${row.deliveryNo}/reassign`, { driverId: to }); if (me) await showPlacesToDriver(me.officeId, to, [...row.pickupPlaceIds, row.dropPlaceId]).catch(() => {}); toast(`${names.driver(to)} に送りました（新しい配送番号 ${r.deliveryNo}）`); onDone(); onClose(); }
    catch (e) { setErr(e instanceof HttpError ? e.message : '送れませんでした'); }
  };
  return (
    <Dialog title={`${row.deliveryNo} を${row.status === 'unassigned' ? '割り当てる' : '付け替える'}`} onClose={onClose}>
      <p style={{ margin: 0 }}>{route(row, names)}</p>
      <div className="field"><label htmlFor="to">送るドライバー（勤務中の人）</label>
        <select id="to" value={to} onChange={(e) => setTo(e.target.value)}><option value="">選んでください</option>{drivers.map((d) => <option key={d.id} value={d.id}>{names.driver(d.id)}</option>)}</select></div>
      <p className="muted" style={{ margin: 0, fontSize: 12 }}>新しい配送番号で送ります。元の配送は「付け替え」になります。</p>
      <LockBanner lock={lock} what="このドライバーのこの日の予定" />
      {err && <p className="err" role="alert">{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={onClose}>やめる</button><button className="btn pri" disabled={!to || !lock.mine} onClick={save}>送る</button></div>
    </Dialog>
  );
}

function CancelDialog({ row, lock, onClose, onDone }: { row: DeliveryRow; lock: EditLock; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const names = useNames();
  const [err, setErr] = useState('');
  const save = async () => {
    try {
      const r = await api.post<{ pickedUp: boolean }>(`/v1/office/deliveries/${row.deliveryNo}/cancel`, {});
      toast(r.pickedUp ? '取り消しました。積んだ荷物は、事務所へ戻すか元の場所へ返す配送を作ってください' : '取り消しました');
      onDone(); onClose();
    } catch (e) { setErr(e instanceof HttpError ? e.message : '取り消せませんでした'); }
  };
  return (
    <Dialog title={`${row.deliveryNo} を取り消しますか？`} onClose={onClose}>
      <p style={{ margin: 0 }}>{route(row, names)}</p>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>ドライバーのアプリに「取り消されました」と出ます。元には戻せません。</p>
      <LockBanner lock={lock} what="このドライバーのこの日の予定" />
      {err && <p className="err" role="alert">{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={onClose}>やめる</button><button className="btn danger" disabled={!lock.mine} onClick={save}>取り消す</button></div>
    </Dialog>
  );
}

/** 配送の状態の表示 */
const LABEL: Record<DeliveryRow['status'], [string, string]> = {
  unassigned: ['未割り当て', 'ng'], unaccepted: ['受付待ち', 'warn'], accepted: ['受付済み', ''], picking: ['積み込み中', ''], picked: ['配達中', 'dark'],
  dropping: ['配達中', 'dark'], dropped: ['完了', 'ok'], canceled: ['取り消し', ''], reassigned: ['付け替え', ''],
};
export function DeliveryStatusPill({ status }: { status: DeliveryRow['status'] }) {
  return <span className={`pill ${LABEL[status][1]}`}>{LABEL[status][0]}</span>;
}
